import {
  SetWorkflowPublicAccessLevelDocument,
  SetWorkflowVisibilityDocument,
  ShareWorkflowDocument,
  ShareWorkflowTeamDocument,
  UnshareWorkflowDocument,
  WorkflowAccessSummaryDocument,
  type WorkflowAccessSummaryQuery,
  WorkflowTeamAccessMembersDocument,
  type WorkflowTeamAccessMembersQuery,
} from "#app/lib/data";
import { useAppData } from "#app/lib/data";

import { useObjectSharing } from "./use-object-sharing";

// Workflow flavor of useObjectSharing — same dialog contract as
// useItemSharing, against the workflow_share table.

const MUTATIONS = {
  share: ShareWorkflowDocument,
  shareTeam: ShareWorkflowTeamDocument,
  unshare: UnshareWorkflowDocument,
  setVisibility: SetWorkflowVisibilityDocument,
  setPublicAccessLevel: SetWorkflowPublicAccessLevelDocument,
};

const selectSummary = (data: WorkflowAccessSummaryQuery | undefined) =>
  data?.workflowAccessSummary ?? null;

const selectTeamMembers = (data: unknown) =>
  (data as WorkflowTeamAccessMembersQuery | undefined)
    ?.workflowTeamAccessMembers ?? [];

const mergePage = (
  prev: WorkflowAccessSummaryQuery,
  next: WorkflowAccessSummaryQuery,
  list: "members" | "teams",
): WorkflowAccessSummaryQuery => {
  const p = prev.workflowAccessSummary;
  const n = next.workflowAccessSummary;
  return {
    ...next,
    workflowAccessSummary: {
      ...n,
      members: list === "members" ? [...p.members, ...n.members] : p.members,
      teams: list === "teams" ? [...p.teams, ...n.teams] : p.teams,
    },
  };
};

export function useWorkflowSharing(
  workflowId: string | null,
  search?: string,
) {
  // app-provider funnels WorkspaceData through a local reducer (not a
  // live useQuery), so a plain Apollo refetch doesn't update the
  // sidebar's workflow list. Hit AppData.refetch() after visibility
  // flips to re-dispatch the payload and rerender consumers (sidebar's
  // lock chip).
  const { refetch: refetchAppData } = useAppData();

  return useObjectSharing({
    id: workflowId,
    search,
    idKey: "workflowId",
    summaryDocument: WorkflowAccessSummaryDocument,
    teamMembersDocument: WorkflowTeamAccessMembersDocument,
    mutations: MUTATIONS,
    selectSummary,
    selectTeamMembers,
    mergePage,
    onVisibilityChange: refetchAppData,
  });
}
